import { useState, useEffect, useMemo } from 'react';
import {
  Sliders,
  TrendingUp,
  Award,
  Layers,
  ShieldAlert,
  RotateCcw,
  Save,
  CheckCircle2,
  AlertTriangle,
  Info,
  Plus,
  X,
  Gauge,
  HelpCircle,
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import {
  useHrSettings,
  useUpdateHrSettings,
  DEFAULT_HR_SETTINGS,
  type HrSettings,
} from '../hooks/useHrSettings';

type TabKey = 'incentives' | 'sales' | 'engineering' | 'grades' | 'tracks';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April (Standard Financial Year)',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function HrPerformanceSettingsPage() {
  const { data: serverSettings, isLoading } = useHrSettings();
  const updateSettingsMut = useUpdateHrSettings();

  const [activeTab, setActiveTab] = useState<TabKey>('incentives');
  const [form, setForm] = useState<HrSettings>(DEFAULT_HR_SETTINGS);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (serverSettings) {
      setForm(serverSettings);
      setIsDirty(false);
    }
  }, [serverSettings]);

  // Tag inputs state
  const [salesDeptInput, setSalesDeptInput] = useState('');
  const [salesRoleInput, setSalesRoleInput] = useState('');
  const [engDeptInput, setEngDeptInput] = useState('');
  const [engRoleInput, setEngRoleInput] = useState('');

  // Validations
  const salesSum =
    Number(form.targetAchievementWeight) +
    Number(form.winRateWeight) +
    Number(form.throughputWeight);
  const isSalesSumValid = salesSum === 100;

  const engSum =
    Number(form.taskCompletionWeight) +
    Number(form.onTimeDeliveryWeight) +
    Number(form.milestoneCompletionWeight);
  const isEngSumValid = engSum === 100;

  const grades = form.gradeThresholds;
  const isGradesDescending =
    grades.APlus > grades.A &&
    grades.A > grades.B &&
    grades.B > grades.C &&
    grades.C > grades.D &&
    grades.D >= 0 &&
    grades.APlus <= 100;

  const isFormValid =
    isSalesSumValid &&
    isEngSumValid &&
    isGradesDescending &&
    form.defaultIncentivePercent >= 0 &&
    form.defaultIncentivePercent <= 100 &&
    form.expectedDealsPerPeriod >= 1 &&
    form.salesDepartments.length > 0 &&
    form.salesRoles.length > 0 &&
    form.engineeringDepartments.length > 0 &&
    form.engineeringRoles.length > 0;

  const handleFieldChange = <K extends keyof HrSettings>(field: K, value: HrSettings[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleGradeChange = (gradeKey: keyof HrSettings['gradeThresholds'], value: number) => {
    setForm((prev) => ({
      ...prev,
      gradeThresholds: {
        ...prev.gradeThresholds,
        [gradeKey]: value,
      },
    }));
    setIsDirty(true);
  };

  const handleAddTag = (
    field: 'salesDepartments' | 'salesRoles' | 'engineeringDepartments' | 'engineeringRoles',
    val: string,
    clearInput: () => void
  ) => {
    const trimmed = val.trim().toLowerCase();
    if (!trimmed) return;
    if (!form[field].includes(trimmed)) {
      handleFieldChange(field, [...form[field], trimmed]);
    }
    clearInput();
  };

  const handleRemoveTag = (
    field: 'salesDepartments' | 'salesRoles' | 'engineeringDepartments' | 'engineeringRoles',
    tagToRemove: string
  ) => {
    handleFieldChange(
      field,
      form[field].filter((t) => t !== tagToRemove)
    );
  };

  const handleResetToDefaults = () => {
    if (confirm('Reset all HR settings and formulas to system defaults?')) {
      setForm(DEFAULT_HR_SETTINGS);
      setIsDirty(true);
    }
  };

  const handleSave = async () => {
    if (!isFormValid) return;
    await updateSettingsMut.mutateAsync(form);
    setIsDirty(false);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[350px] p-8 space-y-3">
        <Spinner />
        <span className="text-sm text-muted-foreground font-medium">Loading HR Settings & Formulas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl pb-12">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded bg-primary/10 text-primary">
              HR Administration
            </span>
            <span className="text-xs text-muted-foreground">• Dynamic Rules & Scoring Engine</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground mt-1 flex items-center gap-2">
            <Sliders className="h-6 w-6 text-primary" />
            Performance & Incentive Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Configure all business constants, score track formulas, grade bands, and incentive rules.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetToDefaults}
            className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Defaults
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isFormValid || updateSettingsMut.isPending || !isDirty}
            className="gap-1.5 text-xs font-semibold shadow-sm"
          >
            {updateSettingsMut.isPending ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Global Validation Warning Banner if anything invalid */}
      {!isFormValid && (
        <div className="p-3.5 rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 text-red-900 dark:text-red-300 text-xs flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <div className="font-semibold">Some settings require correction before saving:</div>
            <ul className="list-disc pl-4 space-y-0.5 text-red-700 dark:text-red-400">
              {!isSalesSumValid && (
                <li>Sales track weights must sum to exactly 100% (currently {salesSum}%).</li>
              )}
              {!isEngSumValid && (
                <li>Engineering track weights must sum to exactly 100% (currently {engSum}%).</li>
              )}
              {!isGradesDescending && (
                <li>Grade thresholds must be strictly descending (A+ &gt; A &gt; B &gt; C &gt; D).</li>
              )}
              {form.expectedDealsPerPeriod < 1 && <li>Expected deals per period must be at least 1.</li>}
            </ul>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-border pb-1 overflow-x-auto no-scrollbar">
        {[
          { key: 'incentives', label: 'Incentives & Carry-Forward', icon: TrendingUp },
          { key: 'sales', label: 'Sales Track Weights', icon: Gauge, badge: !isSalesSumValid ? '!' : undefined },
          { key: 'engineering', label: 'Engineering Track Weights', icon: Layers, badge: !isEngSumValid ? '!' : undefined },
          { key: 'grades', label: 'Grade Bands', icon: Award, badge: !isGradesDescending ? '!' : undefined },
          { key: 'tracks', label: 'Track Resolution & Roles', icon: Sliders },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key as TabKey)}
              className={`flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer select-none ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span>{tab.label}</span>
              {tab.badge && (
                <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab 1: Incentives & Carry-Forward */}
      {activeTab === 'incentives' && (
        <Card className="border border-border/70 shadow-2xs">
          <CardHeader
            title="Incentive Defaults & Financial Period Rules"
            subtitle="Configure default percentage rates and fiscal calendar rules for sales incentives."
          />
          <CardBody className="space-y-6 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Default Incentive % */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground flex items-center justify-between">
                  <span>Default Incentive Rate (%)</span>
                  <span className="text-[11px] text-muted-foreground">Default: 5%</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={form.defaultIncentivePercent}
                    onChange={(e) =>
                      handleFieldChange('defaultIncentivePercent', Math.max(0, Math.min(100, Number(e.target.value))))
                    }
                    className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-medium focus:ring-2 focus:ring-primary/30 outline-none"
                  />
                  <span className="absolute right-3 top-2 text-xs text-muted-foreground">%</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Pre-filled on the Assign Target modal when creating a target without a custom percentage.
                </p>
              </div>

              {/* Financial Year Start Month */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground flex items-center justify-between">
                  <span>Financial Year Start Month</span>
                  <span className="text-[11px] text-muted-foreground">Default: April (Month 4)</span>
                </label>
                <select
                  value={form.financialYearStartMonth}
                  onChange={(e) => handleFieldChange('financialYearStartMonth', Number(e.target.value))}
                  className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-medium focus:ring-2 focus:ring-primary/30 outline-none cursor-pointer"
                >
                  {MONTH_NAMES.map((name, i) => (
                    <option key={i + 1} value={i + 1}>
                      {name} (Month {i + 1})
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  Carry-forward shortfalls cannot cross into a new financial year.
                </p>
              </div>
            </div>

            <div className="border-t border-border pt-5 space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Carry-Forward Settings
              </h3>

              {/* Allow Carry-Forward Toggle */}
              <div className="flex items-start justify-between p-4 rounded-xl border border-border bg-muted/20">
                <div className="space-y-0.5 pr-4">
                  <span className="text-xs font-semibold text-foreground">
                    Allow Carry-Forward of Target Shortfalls
                  </span>
                  <p className="text-[11px] text-muted-foreground">
                    When enabled, sales reps who miss their revenue target in month N can have the shortfall rolled into month N+1 as an effective target. When disabled, the carry-forward button is hidden.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={form.allowCarryForward}
                    onChange={(e) => handleFieldChange('allowCarryForward', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Max Carry Forward Months */}
              <div className="space-y-1.5 max-w-sm">
                <label className="text-xs font-semibold text-foreground flex items-center justify-between">
                  <span>Max Carry-Forward Window</span>
                  <span className="text-[11px] text-muted-foreground">Default: 1 Month</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max="12"
                  value={form.maxCarryForwardMonths}
                  onChange={(e) =>
                    handleFieldChange('maxCarryForwardMonths', Math.max(0, Math.min(12, Number(e.target.value))))
                  }
                  className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-medium focus:ring-2 focus:ring-primary/30 outline-none"
                />
                <p className="text-[11px] text-muted-foreground">
                  Months a shortfall can be forwarded. Standard: 1 month (immediate next period).
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Tab 2: Sales Track Weights */}
      {activeTab === 'sales' && (
        <Card className="border border-border/70 shadow-2xs">
          <CardHeader
            title="Sales Track Scoring Weights & Benchmarks"
            subtitle="Configure metric weights and benchmark standards for BDEs and Sales Representatives."
          />
          <CardBody className="space-y-6 p-6">
            {/* Live Weight Sum Indicator */}
            <div
              className={`p-3.5 rounded-xl border flex items-center justify-between text-xs font-medium ${
                isSalesSumValid
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-300'
                  : 'border-red-200 bg-red-50 text-red-900 dark:bg-red-950/20 dark:text-red-300'
              }`}
            >
              <div className="flex items-center gap-2">
                {isSalesSumValid ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                )}
                <span>
                  Total Sales Metric Weight: <strong>{salesSum}%</strong>{' '}
                  {isSalesSumValid ? '(Valid: sums to 100%)' : '(Invalid: must equal exactly 100%)'}
                </span>
              </div>
              <span className="font-mono text-xs font-bold">
                {form.targetAchievementWeight}% + {form.winRateWeight}% + {form.throughputWeight}% = {salesSum}%
              </span>
            </div>

            {/* Weights Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Target Achievement Weight */}
              <div className="p-4 rounded-xl border border-border bg-background space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground">Target Achievement</span>
                  <span className="text-xs font-mono text-primary font-bold">{form.targetAchievementWeight}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={form.targetAchievementWeight}
                  onChange={(e) => handleFieldChange('targetAchievementWeight', Number(e.target.value))}
                  className="w-full accent-primary cursor-pointer"
                />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  % of revenue collected vs effective target (base + carried-forward shortfall).
                </p>
              </div>

              {/* Win Rate Weight */}
              <div className="p-4 rounded-xl border border-border bg-background space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground">Win Rate</span>
                  <span className="text-xs font-mono text-primary font-bold">{form.winRateWeight}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={form.winRateWeight}
                  onChange={(e) => handleFieldChange('winRateWeight', Number(e.target.value))}
                  className="w-full accent-primary cursor-pointer"
                />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  % of deals won vs total closed deals during the target period.
                </p>
              </div>

              {/* Throughput Weight */}
              <div className="p-4 rounded-xl border border-border bg-background space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground">Throughput</span>
                  <span className="text-xs font-mono text-primary font-bold">{form.throughputWeight}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={form.throughputWeight}
                  onChange={(e) => handleFieldChange('throughputWeight', Number(e.target.value))}
                  className="w-full accent-primary cursor-pointer"
                />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Number of deals won compared to the expected benchmark deals per period.
                </p>
              </div>
            </div>

            {/* Benchmarks Section */}
            <div className="border-t border-border pt-5 space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Sales Benchmarks & Deductions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-foreground flex items-center justify-between">
                    <span>Expected Deals per Month (Benchmark)</span>
                    <span className="text-[11px] text-muted-foreground">Default: 5 deals</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.expectedDealsPerPeriod}
                    onChange={(e) =>
                      handleFieldChange('expectedDealsPerPeriod', Math.max(1, Math.round(Number(e.target.value))))
                    }
                    className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-medium focus:ring-2 focus:ring-primary/30 outline-none"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Denominator for throughput scoring. Rep gets 100% throughput if they close this many deals.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-foreground flex items-center justify-between">
                    <span>Warning Penalty Deduction (Points)</span>
                    <span className="text-[11px] text-muted-foreground">Default: 5 pts / warning</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.warningPenaltyPoints}
                    onChange={(e) =>
                      handleFieldChange('warningPenaltyPoints', Math.max(0, Number(e.target.value)))
                    }
                    className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-medium focus:ring-2 focus:ring-primary/30 outline-none"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Points deducted from final performance score for each disciplinary warning issued in period.
                  </p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Tab 3: Engineering Track Weights */}
      {activeTab === 'engineering' && (
        <Card className="border border-border/70 shadow-2xs">
          <CardHeader
            title="Engineering Track Scoring Weights"
            subtitle="Configure weights for task completion, on-time delivery, and project milestone completion."
          />
          <CardBody className="space-y-6 p-6">
            {/* Live Weight Sum Indicator */}
            <div
              className={`p-3.5 rounded-xl border flex items-center justify-between text-xs font-medium ${
                isEngSumValid
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-300'
                  : 'border-red-200 bg-red-50 text-red-900 dark:bg-red-950/20 dark:text-red-300'
              }`}
            >
              <div className="flex items-center gap-2">
                {isEngSumValid ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                )}
                <span>
                  Total Engineering Metric Weight: <strong>{engSum}%</strong>{' '}
                  {isEngSumValid ? '(Valid: sums to 100%)' : '(Invalid: must equal exactly 100%)'}
                </span>
              </div>
              <span className="font-mono text-xs font-bold">
                {form.taskCompletionWeight}% + {form.onTimeDeliveryWeight}% + {form.milestoneCompletionWeight}% = {engSum}%
              </span>
            </div>

            {/* Weights Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Task Completion Weight */}
              <div className="p-4 rounded-xl border border-border bg-background space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground">Task Completion</span>
                  <span className="text-xs font-mono text-primary font-bold">{form.taskCompletionWeight}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={form.taskCompletionWeight}
                  onChange={(e) => handleFieldChange('taskCompletionWeight', Number(e.target.value))}
                  className="w-full accent-primary cursor-pointer"
                />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  % of assigned tasks marked completed / done during the period.
                </p>
              </div>

              {/* On-Time Delivery Weight */}
              <div className="p-4 rounded-xl border border-border bg-background space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground">On-Time Delivery</span>
                  <span className="text-xs font-mono text-primary font-bold">{form.onTimeDeliveryWeight}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={form.onTimeDeliveryWeight}
                  onChange={(e) => handleFieldChange('onTimeDeliveryWeight', Number(e.target.value))}
                  className="w-full accent-primary cursor-pointer"
                />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  % of completed tasks finished on or before their due date (EOD).
                </p>
              </div>

              {/* Milestone Completion Weight */}
              <div className="p-4 rounded-xl border border-border bg-background space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground">Milestone Completion</span>
                  <span className="text-xs font-mono text-primary font-bold">{form.milestoneCompletionWeight}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={form.milestoneCompletionWeight}
                  onChange={(e) => handleFieldChange('milestoneCompletionWeight', Number(e.target.value))}
                  className="w-full accent-primary cursor-pointer"
                />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  % of project milestones completed vs total scheduled due in the period.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Tab 4: Grade Bands */}
      {activeTab === 'grades' && (
        <Card className="border border-border/70 shadow-2xs">
          <CardHeader
            title="Performance Grade Bands & Thresholds"
            subtitle="Configure minimum scores required for letter grades. Must be in strictly descending order."
          />
          <CardBody className="space-y-6 p-6">
            {/* Visual Grade Ladder */}
            <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-3">
              <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                Grade Scale Preview
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-center text-xs">
                <div className="p-3 rounded-lg bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 font-bold border border-emerald-300">
                  <div className="text-lg">A+</div>
                  <div className="text-[11px] font-normal">≥ {form.gradeThresholds.APlus} pts</div>
                </div>
                <div className="p-3 rounded-lg bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300 font-bold border border-green-300">
                  <div className="text-lg">A</div>
                  <div className="text-[11px] font-normal">≥ {form.gradeThresholds.A} pts</div>
                </div>
                <div className="p-3 rounded-lg bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 font-bold border border-blue-300">
                  <div className="text-lg">B</div>
                  <div className="text-[11px] font-normal">≥ {form.gradeThresholds.B} pts</div>
                </div>
                <div className="p-3 rounded-lg bg-yellow-100 text-yellow-800 dark:bg-yellow-950/60 dark:text-yellow-300 font-bold border border-yellow-300">
                  <div className="text-lg">C</div>
                  <div className="text-[11px] font-normal">≥ {form.gradeThresholds.C} pts</div>
                </div>
                <div className="p-3 rounded-lg bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300 font-bold border border-orange-300">
                  <div className="text-lg">D</div>
                  <div className="text-[11px] font-normal">≥ {form.gradeThresholds.D} pts</div>
                </div>
                <div className="p-3 rounded-lg bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 font-bold border border-red-300">
                  <div className="text-lg">F</div>
                  <div className="text-[11px] font-normal">&lt; {form.gradeThresholds.D} pts</div>
                </div>
              </div>
            </div>

            {/* Threshold Inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">Grade A+ (Min Score)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.gradeThresholds.APlus}
                  onChange={(e) => handleGradeChange('APlus', Number(e.target.value))}
                  className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-semibold text-emerald-600 focus:ring-2 focus:ring-primary/30 outline-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">Grade A (Min Score)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.gradeThresholds.A}
                  onChange={(e) => handleGradeChange('A', Number(e.target.value))}
                  className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-semibold text-green-600 focus:ring-2 focus:ring-primary/30 outline-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">Grade B (Min Score)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.gradeThresholds.B}
                  onChange={(e) => handleGradeChange('B', Number(e.target.value))}
                  className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-semibold text-blue-600 focus:ring-2 focus:ring-primary/30 outline-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">Grade C (Min Score)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.gradeThresholds.C}
                  onChange={(e) => handleGradeChange('C', Number(e.target.value))}
                  className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-semibold text-yellow-600 focus:ring-2 focus:ring-primary/30 outline-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">Grade D (Min Score)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.gradeThresholds.D}
                  onChange={(e) => handleGradeChange('D', Number(e.target.value))}
                  className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm font-semibold text-orange-600 focus:ring-2 focus:ring-primary/30 outline-none"
                />
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Tab 5: Track Resolution */}
      {activeTab === 'tracks' && (
        <Card className="border border-border/70 shadow-2xs">
          <CardHeader
            title="Track Resolution (Sales vs Engineering Routing)"
            subtitle="Define which department names and role keywords map employees to the Sales or Engineering performance scoring tracks."
          />
          <CardBody className="space-y-6 p-6">
            <div className="p-3.5 rounded-xl border border-blue-200 bg-blue-50/60 dark:bg-blue-950/20 text-xs text-blue-900 dark:text-blue-300 flex items-start gap-2.5">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <strong>Resolution Logic:</strong> An employee is evaluated on the <strong>Sales Track</strong> if their department matches any Sales Department or their role contains any Sales Role keyword. Otherwise, if their department matches Engineering Departments or role matches Engineering Roles, they are evaluated on the <strong>Engineering Track</strong>. All other employees are skipped.
              </div>
            </div>

            {/* Sales Track Tags */}
            <div className="p-4 rounded-xl border border-border bg-background space-y-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-purple-600"></span>
                <h3 className="text-sm font-bold text-foreground">Sales Track Routing</h3>
              </div>

              {/* Sales Departments */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Sales Departments (Exact Match)
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {form.salesDepartments.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag('salesDepartments', tag)}
                        className="hover:text-red-500 cursor-pointer"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 max-w-sm mt-1">
                  <input
                    type="text"
                    placeholder="Add department (e.g. sales)..."
                    value={salesDeptInput}
                    onChange={(e) => setSalesDeptInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag('salesDepartments', salesDeptInput, () => setSalesDeptInput(''));
                      }
                    }}
                    className="w-full h-8 px-2.5 text-xs rounded-md border border-input bg-background"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => handleAddTag('salesDepartments', salesDeptInput, () => setSalesDeptInput(''))}
                  >
                    Add
                  </Button>
                </div>
              </div>

              {/* Sales Roles */}
              <div className="space-y-2 pt-2 border-t border-border/50">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Sales Role Keywords (Substring Match)
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {form.salesRoles.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag('salesRoles', tag)}
                        className="hover:text-red-500 cursor-pointer"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 max-w-sm mt-1">
                  <input
                    type="text"
                    placeholder="Add role keyword (e.g. bde, account executive)..."
                    value={salesRoleInput}
                    onChange={(e) => setSalesRoleInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag('salesRoles', salesRoleInput, () => setSalesRoleInput(''));
                      }
                    }}
                    className="w-full h-8 px-2.5 text-xs rounded-md border border-input bg-background"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => handleAddTag('salesRoles', salesRoleInput, () => setSalesRoleInput(''))}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Engineering Track Tags */}
            <div className="p-4 rounded-xl border border-border bg-background space-y-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-600"></span>
                <h3 className="text-sm font-bold text-foreground">Engineering Track Routing</h3>
              </div>

              {/* Eng Departments */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Engineering Departments (Exact Match)
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {form.engineeringDepartments.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag('engineeringDepartments', tag)}
                        className="hover:text-red-500 cursor-pointer"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 max-w-sm mt-1">
                  <input
                    type="text"
                    placeholder="Add department (e.g. tech, engineering)..."
                    value={engDeptInput}
                    onChange={(e) => setEngDeptInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag('engineeringDepartments', engDeptInput, () => setEngDeptInput(''));
                      }
                    }}
                    className="w-full h-8 px-2.5 text-xs rounded-md border border-input bg-background"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => handleAddTag('engineeringDepartments', engDeptInput, () => setEngDeptInput(''))}
                  >
                    Add
                  </Button>
                </div>
              </div>

              {/* Eng Roles */}
              <div className="space-y-2 pt-2 border-t border-border/50">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Engineering Role Keywords (Substring Match)
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {form.engineeringRoles.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-950/40 dark:text-teal-300"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag('engineeringRoles', tag)}
                        className="hover:text-red-500 cursor-pointer"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 max-w-sm mt-1">
                  <input
                    type="text"
                    placeholder="Add role keyword (e.g. developer, engineer)..."
                    value={engRoleInput}
                    onChange={(e) => setEngRoleInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag('engineeringRoles', engRoleInput, () => setEngRoleInput(''));
                      }
                    }}
                    className="w-full h-8 px-2.5 text-xs rounded-md border border-input bg-background"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => handleAddTag('engineeringRoles', engRoleInput, () => setEngRoleInput(''))}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
